/**
 * Tiptap rich-text engine.
 *
 * All Tiptap-specific code lives here — icons, dynamic imports, Editor creation,
 * toolbar building, and popover interactions. The parent rich-text-driver.js
 * delegates to this file (or a future alternative engine) via factory pattern.
 *
 * @typedef {import('../../../../contracts/forms.js').FieldDriverHandle} FieldDriverHandle
 * @typedef {import('../../../../contracts/forms.js').FieldDriverContext} FieldDriverContext
 * @typedef {import('../../../../contracts/forms.js').RichTextToolbarItem} RichTextToolbarItem
 */

// ---------------------------------------------------------------------------
// SVG icons
// ---------------------------------------------------------------------------

/** @type {Record<string, string>} */
const toolbarIcons = {
  bold: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M6 4h8a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z"/><path d="M6 12h9a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z"/></svg>',
  italic: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="19" y1="4" x2="10" y2="4"/><line x1="14" y1="20" x2="5" y2="20"/><line x1="15" y1="4" x2="9" y2="20"/></svg>',
  heading: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 4v16"/><path d="M18 4v16"/><path d="M6 12h12"/></svg>',
  bulletList: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="9" y1="6" x2="20" y2="6"/><line x1="9" y1="12" x2="20" y2="12"/><line x1="9" y1="18" x2="20" y2="18"/><circle cx="4" cy="6" r="1.5" fill="currentColor" stroke="none"/><circle cx="4" cy="12" r="1.5" fill="currentColor" stroke="none"/><circle cx="4" cy="18" r="1.5" fill="currentColor" stroke="none"/></svg>',
  orderedList: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="10" y1="6" x2="21" y2="6"/><line x1="10" y1="12" x2="21" y2="12"/><line x1="10" y1="18" x2="21" y2="18"/><text x="2" y="9" font-size="8" fill="currentColor" stroke="none" font-family="sans-serif">1</text><text x="2" y="15" font-size="8" fill="currentColor" stroke="none" font-family="sans-serif">2</text><text x="2" y="21" font-size="8" fill="currentColor" stroke="none" font-family="sans-serif">3</text></svg>',
  blockquote: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 21c3 0 7-1 7-8V5c0-1.25-.76-2.02-2-2H5c-1.25 0-2 .75-2 2v4c0 1.25.75 2 2 2h2c1 0 1 0 1 1v1c0 2-3 6-4 6z"/><path d="M15 21c3 0 7-1 7-8V5c0-1.25-.76-2.02-2-2h-3c-1.25 0-2 .75-2 2v4c0 1.25.75 2 2 2h2c1 0 1 0 1 1v1c0 2-3 6-4 6z"/></svg>',
  link: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>',
  image: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>',
  youtube: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="4" width="20" height="16" rx="4"/><polygon points="10,8 16,12 10,16" fill="currentColor" stroke="none"/></svg>',
  sourceView: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>',
  alignLeft: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="15" y2="12"/><line x1="3" y1="18" x2="18" y2="18"/></svg>',
  alignCenter: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="6" y1="12" x2="18" y2="12"/><line x1="4" y1="18" x2="20" y2="18"/></svg>',
  alignRight: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="9" y1="12" x2="21" y2="12"/><line x1="6" y1="18" x2="21" y2="18"/></svg>',
  alignJustify: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>',
  color: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3L4 19h16L12 3z"/><line x1="4" y1="21" x2="20" y2="21" stroke-width="3"/></svg>',
  table: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="3" y1="15" x2="21" y2="15"/><line x1="9" y1="3" x2="9" y2="21"/><line x1="15" y1="3" x2="15" y2="21"/></svg>',
};

import { DEFAULT_RICH_TEXT_TOOLBAR } from '../../../../core/rich-text/toolbar-defaults.js';
import { createLogger } from '../../../../core/logging/logger.js';

const log = createLogger({ module: 'tiptap-engine' });

// ---------------------------------------------------------------------------
// URL validation helpers
// ---------------------------------------------------------------------------

/** @type {string[]} */
const HREF_PROTOCOLS = ['https:', 'http:', 'mailto:', 'tel:'];
/** @type {string[]} */
const SRC_PROTOCOLS = ['https:', 'http:'];

/**
 * @param {string} url
 * @param {string[]} allowed
 * @returns {boolean}
 */
function isUrlAllowed(url, allowed) {
  try {
    // mailto: and tel: are opaque — URL constructor may fail on them
    if (/^mailto:/i.test(url)) return allowed.includes('mailto:');
    if (/^tel:/i.test(url)) return allowed.includes('tel:');
    const parsed = new URL(url);
    return allowed.includes(parsed.protocol);
  } catch {
    return false;
  }
}

const YOUTUBE_PATTERN = /^(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)/i;

// ---------------------------------------------------------------------------
// Color palette
// ---------------------------------------------------------------------------

const COLOR_PALETTE = [
  '#000000', '#555555', '#e53935', '#fb8c00', '#fdd835',
  '#43a047', '#00897b', '#1e88e5', '#8e24aa', '#d81b60',
];

// ---------------------------------------------------------------------------
// Toolbar group definitions (for separator placement)
// ---------------------------------------------------------------------------

/** @type {RichTextToolbarItem[][]} */
const TOOLBAR_GROUPS = [
  ['bold', 'italic', 'color'],
  ['heading', 'blockquote', 'sourceView'],
  ['alignLeft', 'alignCenter', 'alignRight', 'alignJustify'],
  ['bulletList', 'orderedList'],
  ['link', 'image', 'youtube'],
  ['table'],
];

// ---------------------------------------------------------------------------
// Popover infrastructure
// ---------------------------------------------------------------------------

/**
 * @typedef {Object} PopoverHandle
 * @property {() => void} show
 * @property {() => void} hide
 * @property {() => void} destroy
 * @property {HTMLElement} el
 * @property {boolean} isOpen
 */

/**
 * Creates a toolbar popover anchored below a button.
 *
 * @param {HTMLElement} anchorEl
 * @param {{ onHide?: () => void }} [options]
 * @returns {PopoverHandle}
 */
function createToolbarPopover(anchorEl, options) {
  const el = document.createElement('div');
  el.className = 'rich-text-driver__popover';
  el.style.position = 'fixed';
  el.style.zIndex = '9999';
  el.style.display = 'none';
  document.body.appendChild(el);

  let isOpen = false;

  function position() {
    const rect = anchorEl.getBoundingClientRect();
    el.style.left = `${rect.left}px`;
    el.style.top = `${rect.bottom + 4}px`;
    // Flip above if overflows viewport bottom
    requestAnimationFrame(() => {
      const popRect = el.getBoundingClientRect();
      if (popRect.bottom > window.innerHeight) {
        el.style.top = `${rect.top - popRect.height - 4}px`;
      }
      // Flip left if overflows viewport right
      if (popRect.right > window.innerWidth) {
        el.style.left = `${window.innerWidth - popRect.width - 8}px`;
      }
    });
  }

  function handleReposition() {
    if (isOpen) position();
  }

  /** @param {MouseEvent} e */
  function handleClickOutside(e) {
    if (!isOpen) return;
    const target = /** @type {Node} */ (e.target);
    if (!el.contains(target) && !anchorEl.contains(target)) {
      hide();
    }
  }

  /** @param {KeyboardEvent} e */
  function handleEscape(e) {
    if (!isOpen) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation(); // prevent crud-editor from closing
      hide();
    }
  }

  /** @param {KeyboardEvent} e */
  function handleTabTrap(e) {
    if (!isOpen || e.key !== 'Tab') return;
    e.preventDefault();
    e.stopPropagation(); // prevent crud-editor Tab handler from interfering

    const focusable = /** @type {HTMLElement[]} */ (
      [...el.querySelectorAll('input:not(:disabled), button:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"]):not(:disabled)')]
    );
    if (focusable.length === 0) return;

    const currentIdx = focusable.indexOf(/** @type {HTMLElement} */ (document.activeElement));
    let nextIdx;
    if (e.shiftKey) {
      nextIdx = currentIdx <= 0 ? focusable.length - 1 : currentIdx - 1;
    } else {
      nextIdx = currentIdx >= focusable.length - 1 ? 0 : currentIdx + 1;
    }
    focusable[nextIdx].focus();
  }

  function show() {
    if (isOpen) return;
    isOpen = true;
    handle.isOpen = true;
    el.style.display = '';
    position();
    // Bind listeners
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape, true);
    el.addEventListener('keydown', handleTabTrap);
    window.addEventListener('scroll', handleReposition, { passive: true, capture: true });
    window.addEventListener('resize', handleReposition, { passive: true });
    // Focus first focusable element
    setTimeout(() => {
      const first = /** @type {HTMLElement | null} */ (
        el.querySelector('input:not(:disabled), button:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"]):not(:disabled)')
      );
      if (first) first.focus();
    }, 0);
  }

  function hide() {
    if (!isOpen) return;
    isOpen = false;
    handle.isOpen = false;
    el.style.display = 'none';
    // Unbind listeners
    document.removeEventListener('mousedown', handleClickOutside);
    document.removeEventListener('keydown', handleEscape, true);
    el.removeEventListener('keydown', handleTabTrap);
    window.removeEventListener('scroll', handleReposition, /** @type {any} */ ({ capture: true }));
    window.removeEventListener('resize', handleReposition);
    // Return focus to anchor
    anchorEl.focus();
    options?.onHide?.();
  }

  function destroy() {
    hide();
    el.remove();
  }

  const handle = { show, hide, destroy, el, isOpen };
  return handle;
}

// ---------------------------------------------------------------------------
// Popover builders
// ---------------------------------------------------------------------------

/**
 * Builds link popover content.
 * @param {HTMLElement} popoverEl
 * @param {{ onApply: (href: string, newTab: boolean) => void, onRemove: () => void, onCancel: () => void, currentHref?: string, currentTarget?: string }} opts
 */
function buildLinkPopover(popoverEl, opts) {
  popoverEl.innerHTML = '';
  const form = document.createElement('div');
  form.className = 'rich-text-driver__popover-form';

  const urlInput = document.createElement('input');
  urlInput.type = 'text';
  urlInput.className = 'rich-text-driver__popover-input';
  urlInput.placeholder = 'https://example.com';
  urlInput.value = opts.currentHref ?? '';

  const checkLabel = document.createElement('label');
  checkLabel.className = 'rich-text-driver__popover-check-label';
  const checkbox = document.createElement('input');
  checkbox.type = 'checkbox';
  checkbox.checked = opts.currentTarget === '_blank';
  checkLabel.appendChild(checkbox);
  checkLabel.appendChild(document.createTextNode(' Open in new tab'));

  const errorEl = document.createElement('div');
  errorEl.className = 'rich-text-driver__popover-error';

  const actions = document.createElement('div');
  actions.className = 'rich-text-driver__popover-actions';

  const applyBtn = document.createElement('button');
  applyBtn.type = 'button';
  applyBtn.className = 'rich-text-driver__popover-btn rich-text-driver__popover-btn--primary';
  applyBtn.textContent = 'Apply';

  const removeBtn = document.createElement('button');
  removeBtn.type = 'button';
  removeBtn.className = 'rich-text-driver__popover-btn rich-text-driver__popover-btn--danger';
  removeBtn.textContent = 'Remove link';
  if (!opts.currentHref) removeBtn.style.display = 'none';

  function tryApply() {
    const url = urlInput.value.trim();
    if (!url) { errorEl.textContent = 'URL is required'; return; }
    if (!isUrlAllowed(url, HREF_PROTOCOLS)) {
      errorEl.textContent = 'Only http, https, mailto, and tel URLs are allowed';
      return;
    }
    errorEl.textContent = '';
    opts.onApply(url, checkbox.checked);
  }

  applyBtn.addEventListener('click', tryApply);
  urlInput.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') { e.preventDefault(); tryApply(); }
  });
  removeBtn.addEventListener('click', () => opts.onRemove());

  actions.appendChild(applyBtn);
  actions.appendChild(removeBtn);

  form.appendChild(urlInput);
  form.appendChild(checkLabel);
  form.appendChild(errorEl);
  form.appendChild(actions);
  popoverEl.appendChild(form);
}

/**
 * Builds image popover content (URL-only fallback, or alt-text-only when prefillUrl provided).
 * @param {HTMLElement} popoverEl
 * @param {{ onApply: (src: string, alt: string) => void, onCancel: () => void, prefillUrl?: string }} opts
 */
function buildImagePopover(popoverEl, opts) {
  popoverEl.innerHTML = '';
  const form = document.createElement('div');
  form.className = 'rich-text-driver__popover-form';

  const urlInput = document.createElement('input');
  urlInput.type = 'text';
  urlInput.className = 'rich-text-driver__popover-input';
  urlInput.placeholder = 'https://example.com/image.jpg';
  if (opts.prefillUrl) {
    urlInput.value = opts.prefillUrl;
    urlInput.readOnly = true;
    urlInput.classList.add('rich-text-driver__popover-input--readonly');
  }

  const altInput = document.createElement('input');
  altInput.type = 'text';
  altInput.className = 'rich-text-driver__popover-input';
  altInput.placeholder = 'Alt text (describe the image)';

  const checkLabel = document.createElement('label');
  checkLabel.className = 'rich-text-driver__popover-check-label';
  const decorativeCheck = document.createElement('input');
  decorativeCheck.type = 'checkbox';
  checkLabel.appendChild(decorativeCheck);
  checkLabel.appendChild(document.createTextNode(' Decorative image (no alt text)'));
  decorativeCheck.addEventListener('change', () => {
    altInput.disabled = decorativeCheck.checked;
    if (decorativeCheck.checked) altInput.value = '';
  });

  const errorEl = document.createElement('div');
  errorEl.className = 'rich-text-driver__popover-error';

  const actions = document.createElement('div');
  actions.className = 'rich-text-driver__popover-actions';
  const applyBtn = document.createElement('button');
  applyBtn.type = 'button';
  applyBtn.className = 'rich-text-driver__popover-btn rich-text-driver__popover-btn--primary';
  applyBtn.textContent = 'Insert';

  function tryApply() {
    const url = urlInput.value.trim();
    if (!url) { errorEl.textContent = 'URL is required'; return; }
    // Skip URL validation when pre-filled from media picker (server-provided, may be relative)
    if (!opts.prefillUrl && !isUrlAllowed(url, SRC_PROTOCOLS)) {
      errorEl.textContent = 'Only http and https URLs are allowed';
      return;
    }
    const alt = decorativeCheck.checked ? '' : altInput.value.trim();
    if (!decorativeCheck.checked && !alt) {
      errorEl.textContent = 'Alt text is required (or mark as decorative)';
      return;
    }
    errorEl.textContent = '';
    opts.onApply(url, alt);
  }

  applyBtn.addEventListener('click', tryApply);
  urlInput.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') { e.preventDefault(); tryApply(); }
  });

  actions.appendChild(applyBtn);

  form.appendChild(urlInput);
  form.appendChild(altInput);
  form.appendChild(checkLabel);
  form.appendChild(errorEl);
  form.appendChild(actions);
  popoverEl.appendChild(form);
}

/**
 * Builds YouTube popover content.
 * @param {HTMLElement} popoverEl
 * @param {{ onApply: (src: string) => void, onCancel: () => void }} opts
 */
function buildYoutubePopover(popoverEl, opts) {
  popoverEl.innerHTML = '';
  const form = document.createElement('div');
  form.className = 'rich-text-driver__popover-form';

  const urlInput = document.createElement('input');
  urlInput.type = 'text';
  urlInput.className = 'rich-text-driver__popover-input';
  urlInput.placeholder = 'https://www.youtube.com/watch?v=...';

  const errorEl = document.createElement('div');
  errorEl.className = 'rich-text-driver__popover-error';

  const actions = document.createElement('div');
  actions.className = 'rich-text-driver__popover-actions';
  const applyBtn = document.createElement('button');
  applyBtn.type = 'button';
  applyBtn.className = 'rich-text-driver__popover-btn rich-text-driver__popover-btn--primary';
  applyBtn.textContent = 'Insert';

  function tryApply() {
    const url = urlInput.value.trim();
    if (!url) { errorEl.textContent = 'URL is required'; return; }
    if (!YOUTUBE_PATTERN.test(url)) {
      errorEl.textContent = 'Please enter a valid YouTube URL';
      return;
    }
    errorEl.textContent = '';
    opts.onApply(url);
  }

  applyBtn.addEventListener('click', tryApply);
  urlInput.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') { e.preventDefault(); tryApply(); }
  });

  actions.appendChild(applyBtn);
  form.appendChild(urlInput);
  form.appendChild(errorEl);
  form.appendChild(actions);
  popoverEl.appendChild(form);
}

/**
 * Builds color palette popover content.
 * @param {HTMLElement} popoverEl
 * @param {{ onSelect: (color: string) => void, onRemove: () => void }} opts
 */
function buildColorPopover(popoverEl, opts) {
  popoverEl.innerHTML = '';
  const grid = document.createElement('div');
  grid.className = 'rich-text-driver__color-grid';

  for (const color of COLOR_PALETTE) {
    const swatch = document.createElement('button');
    swatch.type = 'button';
    swatch.className = 'rich-text-driver__color-swatch';
    swatch.style.backgroundColor = color;
    swatch.title = color;
    swatch.setAttribute('aria-label', `Color ${color}`);
    swatch.addEventListener('click', () => opts.onSelect(color));
    grid.appendChild(swatch);
  }

  const removeBtn = document.createElement('button');
  removeBtn.type = 'button';
  removeBtn.className = 'rich-text-driver__popover-btn';
  removeBtn.textContent = 'Remove color';
  removeBtn.addEventListener('click', () => opts.onRemove());

  popoverEl.appendChild(grid);
  popoverEl.appendChild(removeBtn);
}

/**
 * Builds heading selector popover content.
 * @param {HTMLElement} popoverEl
 * @param {{ onSelect: (level: 0 | 2 | 3 | 4) => void, activeLevel: number }} opts
 */
function buildHeadingPopover(popoverEl, opts) {
  popoverEl.innerHTML = '';
  const menu = document.createElement('div');
  menu.className = 'rich-text-driver__popover-menu';
  menu.setAttribute('role', 'menu');

  /** @type {{ label: string, level: 0 | 2 | 3 | 4 }[]} */
  const items = [
    { label: 'Paragraph', level: 0 },
    { label: 'Heading 2', level: 2 },
    { label: 'Heading 3', level: 3 },
    { label: 'Heading 4', level: 4 },
  ];

  /** @type {HTMLButtonElement[]} */
  const buttons = [];
  let highlightIdx = 0;

  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'rich-text-driver__popover-menu-item';
    btn.setAttribute('role', 'menuitem');
    btn.textContent = item.label;
    if (item.level === opts.activeLevel || (item.level === 0 && opts.activeLevel === 0)) {
      btn.classList.add('rich-text-driver__popover-menu-item--active');
      highlightIdx = i;
    }
    btn.tabIndex = i === highlightIdx ? 0 : -1;
    btn.addEventListener('click', () => opts.onSelect(item.level));
    buttons.push(btn);
    menu.appendChild(btn);
  }

  // Arrow key navigation within menu
  menu.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      highlightIdx = Math.min(highlightIdx + 1, buttons.length - 1);
      buttons[highlightIdx].focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      highlightIdx = Math.max(highlightIdx - 1, 0);
      buttons[highlightIdx].focus();
    } else if (e.key === 'Home') {
      e.preventDefault();
      highlightIdx = 0;
      buttons[0].focus();
    } else if (e.key === 'End') {
      e.preventDefault();
      highlightIdx = buttons.length - 1;
      buttons[highlightIdx].focus();
    }
  });

  popoverEl.appendChild(menu);
}

/**
 * Builds table action menu popover content.
 * @param {HTMLElement} popoverEl
 * @param {{ editor: import('@tiptap/core').Editor, onDone: () => void }} opts
 */
function buildTablePopover(popoverEl, opts) {
  popoverEl.innerHTML = '';
  const menu = document.createElement('div');
  menu.className = 'rich-text-driver__popover-menu';
  menu.setAttribute('role', 'menu');

  const ed = opts.editor;
  const inTable = ed.can().deleteTable();

  /** @type {{ label: string, action: () => void, disabled: boolean, separator?: boolean }[]} */
  const items = [
    { label: 'Insert table', action: () => ed.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run(), disabled: inTable },
    { label: 'Add row before', action: () => ed.chain().focus().addRowBefore().run(), disabled: !inTable, separator: true },
    { label: 'Add row after', action: () => ed.chain().focus().addRowAfter().run(), disabled: !inTable },
    { label: 'Delete row', action: () => ed.chain().focus().deleteRow().run(), disabled: !inTable },
    { label: 'Add column before', action: () => ed.chain().focus().addColumnBefore().run(), disabled: !inTable, separator: true },
    { label: 'Add column after', action: () => ed.chain().focus().addColumnAfter().run(), disabled: !inTable },
    { label: 'Delete column', action: () => ed.chain().focus().deleteColumn().run(), disabled: !inTable },
    { label: 'Delete table', action: () => ed.chain().focus().deleteTable().run(), disabled: !inTable, separator: true },
  ];

  /** @type {HTMLButtonElement[]} */
  const buttons = [];
  let highlightIdx = 0;

  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    if (item.separator) {
      const sep = document.createElement('div');
      sep.className = 'rich-text-driver__popover-menu-separator';
      sep.setAttribute('role', 'separator');
      menu.appendChild(sep);
    }
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'rich-text-driver__popover-menu-item';
    btn.setAttribute('role', 'menuitem');
    btn.textContent = item.label;
    btn.disabled = item.disabled;
    if (item.disabled) btn.classList.add('rich-text-driver__popover-menu-item--disabled');
    btn.tabIndex = i === 0 ? 0 : -1;
    btn.addEventListener('click', () => {
      item.action();
      opts.onDone();
    });
    buttons.push(btn);
    menu.appendChild(btn);
  }

  // Arrow key navigation
  menu.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      highlightIdx = Math.min(highlightIdx + 1, buttons.length - 1);
      buttons[highlightIdx].focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      highlightIdx = Math.max(highlightIdx - 1, 0);
      buttons[highlightIdx].focus();
    } else if (e.key === 'Home') {
      e.preventDefault();
      highlightIdx = 0;
      buttons[0].focus();
    } else if (e.key === 'End') {
      e.preventDefault();
      highlightIdx = buttons.length - 1;
      buttons[highlightIdx].focus();
    }
  });

  popoverEl.appendChild(menu);
}

// ---------------------------------------------------------------------------
// Engine mount
// ---------------------------------------------------------------------------

/**
 * Mounts a Tiptap-based rich-text editor.
 *
 * @param {HTMLElement} container
 * @param {FieldDriverContext} ctx
 * @returns {FieldDriverHandle}
 */
export function mount(container, ctx) {
  const { def, onChange } = ctx;
  const rtDef = /** @type {import('../../../../contracts/forms.js').RichTextFieldDef} */ (def);

  const wrapper = document.createElement('div');
  wrapper.className = 'rich-text-driver';
  container.appendChild(wrapper);

  /** @type {import('@tiptap/core').Editor | null} */
  let editor = null;
  let editorReady = false;
  let destroyed = false;
  let ignoringUpdate = false;
  let forcedDisabled = false;
  let inSourceMode = false;
  /** @type {HTMLTextAreaElement | null} */
  let sourceTextarea = null;

  /** @type {string} */
  let pendingValue = ctx.value != null ? String(ctx.value) : '';

  /** @type {PopoverHandle[]} */
  const popovers = [];

  function applyDisabled() {
    if (!editor) return;
    const effectiveDisabled = forcedDisabled || Boolean(def.disabled) || !editorReady;
    editor.setEditable(!effectiveDisabled);
    wrapper.classList.toggle('rich-text-driver--disabled', effectiveDisabled);
    if (sourceTextarea) sourceTextarea.disabled = effectiveDisabled;
  }

  // Resolve toolbar config
  const toolbar = rtDef.toolbar ?? DEFAULT_RICH_TEXT_TOOLBAR;

  // Warn about unknown toolbar items (helps catch typos in field defs)
  const knownItems = new Set(TOOLBAR_GROUPS.flat());
  for (const item of toolbar) {
    if (!knownItems.has(item)) {
      log.warn(`Unknown toolbar item: '${item}' — ignored.`);
    }
  }

  // Determine which extra extensions are needed
  const needsAlign = toolbar.some(t => ['alignLeft', 'alignCenter', 'alignRight', 'alignJustify'].includes(t));
  const needsColor = toolbar.includes('color');
  const needsTable = toolbar.includes('table');

  // Build dynamic import list — always import core set
  // Note: StarterKit v3 bundles Link, so we do NOT import it separately.
  /** @type {Promise<any>[]} */
  const imports = [
    import('@tiptap/core'),
    import('@tiptap/starter-kit'),
    import('@tiptap/extension-image'),
    import('@tiptap/extension-youtube'),
  ];

  // Conditional imports based on toolbar config
  if (needsAlign) imports.push(import('@tiptap/extension-text-align'));
  if (needsColor) {
    imports.push(import('@tiptap/extension-text-style'));
    imports.push(import('@tiptap/extension-color'));
  }
  if (needsTable) {
    imports.push(import('@tiptap/extension-table'));
    imports.push(import('@tiptap/extension-table-row'));
    imports.push(import('@tiptap/extension-table-cell'));
    imports.push(import('@tiptap/extension-table-header'));
  }

  Promise.all(imports).then((modules) => {
    if (destroyed) return;

    // Destructure core modules (always at indices 0-3)
    // Use `mod.default ?? mod.Name` to support both Tiptap v2 (default) and v3 (named) exports
    const { Editor } = modules[0];
    const StarterKit = modules[1].default ?? modules[1].StarterKit;
    const Image = modules[2].default ?? modules[2].Image;
    const Youtube = modules[3].default ?? modules[3].Youtube;

    // Conditional modules start at index 4
    let modIdx = 4;
    /** @type {any} */ let TextAlign;
    /** @type {any} */ let TextStyle;
    /** @type {any} */ let Color;
    /** @type {any} */ let Table;
    /** @type {any} */ let TableRow;
    /** @type {any} */ let TableCell;
    /** @type {any} */ let TableHeader;

    if (needsAlign) { const m = modules[modIdx++]; TextAlign = m.default ?? m.TextAlign; }
    if (needsColor) {
      const ms = modules[modIdx++]; TextStyle = ms.default ?? ms.TextStyle;
      const mc = modules[modIdx++]; Color = mc.default ?? mc.Color;
    }
    if (needsTable) {
      const mt = modules[modIdx++]; Table = mt.default ?? mt.Table;
      const mr = modules[modIdx++]; TableRow = mr.default ?? mr.TableRow;
      const mc = modules[modIdx++]; TableCell = mc.default ?? mc.TableCell;
      const mh = modules[modIdx++]; TableHeader = mh.default ?? mh.TableHeader;
    }

    // Build extension list
    // Link is bundled in StarterKit v3 — configure via StarterKit options
    const starterKitOpts = toolbar.includes('link')
      ? { link: { openOnClick: false } }
      : { link: false };
    /** @type {import('@tiptap/core').AnyExtension[]} */
    const extensions = [StarterKit.configure(starterKitOpts)];

    if (toolbar.includes('image') || toolbar.includes('youtube')) extensions.push(Image);
    if (toolbar.includes('youtube')) extensions.push(Youtube);
    if (needsAlign) extensions.push(TextAlign.configure({ types: ['heading', 'paragraph'] }));
    if (needsColor) { extensions.push(TextStyle); extensions.push(Color); }
    if (needsTable) {
      extensions.push(Table.configure({ resizable: false }));
      extensions.push(TableRow);
      extensions.push(TableCell);
      extensions.push(TableHeader);
    }

    editor = new Editor({
      element: wrapper,
      extensions,
      content: pendingValue,
      editable: !(forcedDisabled || Boolean(def.disabled)),
      onUpdate(/** @type {{ editor: import('@tiptap/core').Editor }} */ { editor: ed }) {
        if (ignoringUpdate) return;
        onChange(ed.getHTML());
      },
    });

    // Build toolbar UI
    if (toolbar.length > 0 && editor) {
      const ed = /** @type {import('@tiptap/core').Editor} */ (editor);
      const toolbarEl = document.createElement('div');
      toolbarEl.className = 'rich-text-driver__toolbar';
      toolbarEl.setAttribute('role', 'toolbar');
      wrapper.insertBefore(toolbarEl, wrapper.firstChild);

      // Track all toolbar buttons for roving tabindex
      /** @type {HTMLButtonElement[]} */
      const allBtns = [];
      let rovingIdx = 0;

      /**
       * @param {import('../../../../contracts/forms.js').RichTextToolbarItem} cmd
       * @param {(btn: HTMLButtonElement) => void} action
       * @param {{ toggle?: boolean }} [opts]
       * @returns {HTMLButtonElement | null}
       */
      function addBtn(cmd, action, opts) {
        if (!toolbar.includes(cmd)) return null;
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'rich-text-driver__toolbar-btn';
        btn.dataset.cmd = cmd;
        btn.tabIndex = allBtns.length === 0 ? 0 : -1;
        if (toolbarIcons[cmd]) {
          btn.innerHTML = toolbarIcons[cmd];
          btn.title = cmd;
          btn.setAttribute('aria-label', cmd);
        } else {
          btn.textContent = cmd;
        }
        if (opts?.toggle) {
          btn.setAttribute('aria-pressed', 'false');
        }
        btn.addEventListener('mousedown', (e) => {
          e.preventDefault(); // keep editor focus
          action(btn);
        });
        toolbarEl.appendChild(btn);
        allBtns.push(btn);
        return btn;
      }

      /** Inserts a visual separator */
      function addSep() {
        const sep = document.createElement('span');
        sep.className = 'rich-text-driver__toolbar-sep';
        sep.setAttribute('role', 'separator');
        toolbarEl.appendChild(sep);
      }

      // Determine which groups have visible items
      let prevGroupHadItems = false;

      for (const group of TOOLBAR_GROUPS) {
        const groupHasItems = group.some(item => toolbar.includes(item));
        if (!groupHasItems) continue;
        if (prevGroupHadItems) addSep();
        prevGroupHadItems = true;

        for (const item of group) {
          switch (item) {
            case 'bold':
              addBtn('bold', () => ed.chain().focus().toggleBold().run(), { toggle: true });
              break;
            case 'italic':
              addBtn('italic', () => ed.chain().focus().toggleItalic().run(), { toggle: true });
              break;
            case 'color': {
              addBtn('color', (btn) => {
                // Close other popovers
                popovers.forEach(p => p.hide());
                const pop = createToolbarPopover(btn);
                popovers.push(pop);
                buildColorPopover(pop.el, {
                  onSelect(color) {
                    ed.chain().focus().setColor(color).run();
                    pop.hide();
                  },
                  onRemove() {
                    ed.chain().focus().unsetColor().run();
                    pop.hide();
                  },
                });
                pop.show();
              });
              break;
            }
            case 'heading': {
              addBtn('heading', (btn) => {
                popovers.forEach(p => p.hide());
                const pop = createToolbarPopover(btn);
                popovers.push(pop);
                // Determine current heading level
                let activeLevel = 0;
                for (const lvl of [2, 3, 4]) {
                  if (ed.isActive('heading', { level: lvl })) { activeLevel = lvl; break; }
                }
                buildHeadingPopover(pop.el, {
                  activeLevel,
                  onSelect(level) {
                    if (level === 0) {
                      ed.chain().focus().setParagraph().run();
                    } else {
                      ed.chain().focus().toggleHeading({ level }).run();
                    }
                    pop.hide();
                  },
                });
                pop.show();
              }, { toggle: true });
              break;
            }
            case 'blockquote':
              addBtn('blockquote', () => ed.chain().focus().toggleBlockquote().run(), { toggle: true });
              break;
            case 'sourceView': {
              addBtn('sourceView', (btn) => {
                inSourceMode = !inSourceMode;
                btn.classList.toggle('rich-text-driver__toolbar-btn--active', inSourceMode);
                btn.setAttribute('aria-pressed', inSourceMode ? 'true' : 'false');
                const proseMirrorEl = wrapper.querySelector('.ProseMirror');
                if (inSourceMode) {
                  if (!sourceTextarea) {
                    sourceTextarea = document.createElement('textarea');
                    sourceTextarea.className = 'rich-text-driver__source';
                    const effectiveDisabled = forcedDisabled || Boolean(def.disabled) || !editorReady;
                    sourceTextarea.disabled = effectiveDisabled;
                    wrapper.appendChild(sourceTextarea);
                  }
                  sourceTextarea.value = ed.getHTML();
                  if (proseMirrorEl) /** @type {HTMLElement} */ (proseMirrorEl).style.display = 'none';
                  sourceTextarea.style.display = 'block';
                  sourceTextarea.focus();
                } else {
                  if (sourceTextarea) {
                    const html = sourceTextarea.value;
                    ignoringUpdate = true;
                    try {
                      ed.commands.setContent(html, { emitUpdate: false });
                    } finally {
                      ignoringUpdate = false;
                    }
                    onChange(ed.getHTML());
                    sourceTextarea.style.display = 'none';
                  }
                  if (proseMirrorEl) /** @type {HTMLElement} */ (proseMirrorEl).style.display = '';
                  ed.commands.focus();
                }
              }, { toggle: true });
              break;
            }
            case 'alignLeft':
              addBtn('alignLeft', () => ed.chain().focus().setTextAlign('left').run(), { toggle: true });
              break;
            case 'alignCenter':
              addBtn('alignCenter', () => ed.chain().focus().setTextAlign('center').run(), { toggle: true });
              break;
            case 'alignRight':
              addBtn('alignRight', () => ed.chain().focus().setTextAlign('right').run(), { toggle: true });
              break;
            case 'alignJustify':
              addBtn('alignJustify', () => ed.chain().focus().setTextAlign('justify').run(), { toggle: true });
              break;
            case 'bulletList':
              addBtn('bulletList', () => ed.chain().focus().toggleBulletList().run(), { toggle: true });
              break;
            case 'orderedList':
              addBtn('orderedList', () => ed.chain().focus().toggleOrderedList().run(), { toggle: true });
              break;
            case 'link': {
              addBtn('link', (btn) => {
                popovers.forEach(p => p.hide());
                const pop = createToolbarPopover(btn);
                popovers.push(pop);
                const attrs = ed.getAttributes('link');
                buildLinkPopover(pop.el, {
                  currentHref: attrs.href,
                  currentTarget: attrs.target,
                  onApply(href, newTab) {
                    const linkAttrs = newTab
                      ? { href, target: '_blank', rel: 'noopener' }
                      : { href, target: null, rel: null };
                    ed.chain().focus().setLink(linkAttrs).run();
                    pop.hide();
                  },
                  onRemove() {
                    ed.chain().focus().unsetLink().run();
                    pop.hide();
                  },
                  onCancel() { pop.hide(); },
                });
                pop.show();
              }, { toggle: true });
              break;
            }
            case 'image': {
              addBtn('image', (btn) => {
                // Priority: mediaClient/uploadClient → media picker; fallback → URL popover
                if (ctx.mediaClient || ctx.uploadClient) {
                  import('../../../../core/media/media-picker.js').then(({ mediaPickerDialog }) => {
                    if (destroyed) return;
                    mediaPickerDialog({
                      uploadClient: ctx.uploadClient,
                      mediaClient: ctx.mediaClient,
                      accept: 'image/*',
                    }).then((result) => {
                      if (!result || destroyed) return;
                      // Show alt-text popover with pre-filled URL from media picker
                      showImageAltPopover(btn, ed, result.url);
                    });
                  }).catch(() => {
                    // Fallback to URL popover if media picker fails to load
                    showImageUrlPopover(btn, ed);
                  });
                } else {
                  showImageUrlPopover(btn, ed);
                }
              });
              break;
            }
            case 'youtube': {
              addBtn('youtube', (btn) => {
                popovers.forEach(p => p.hide());
                const pop = createToolbarPopover(btn);
                popovers.push(pop);
                buildYoutubePopover(pop.el, {
                  onApply(src) {
                    ed.chain().focus().setYoutubeVideo({ src }).run();
                    pop.hide();
                  },
                  onCancel() { pop.hide(); },
                });
                pop.show();
              });
              break;
            }
            case 'table': {
              addBtn('table', (btn) => {
                popovers.forEach(p => p.hide());
                const pop = createToolbarPopover(btn);
                popovers.push(pop);
                buildTablePopover(pop.el, {
                  editor: ed,
                  onDone() { pop.hide(); },
                });
                pop.show();
              });
              break;
            }
          }
        }
      }

      /**
       * Shows alt-text popover after media picker selection.
       * @param {HTMLButtonElement} btn
       * @param {import('@tiptap/core').Editor} edRef
       * @param {string} imageUrl - Pre-selected image URL from media picker.
       */
      function showImageAltPopover(btn, edRef, imageUrl) {
        popovers.forEach(p => p.hide());
        const pop = createToolbarPopover(btn);
        popovers.push(pop);
        buildImagePopover(pop.el, {
          onApply(_src, alt) {
            edRef.chain().focus().setImage({ src: imageUrl, alt }).run();
            pop.hide();
          },
          onCancel() { pop.hide(); },
          prefillUrl: imageUrl,
        });
        pop.show();
      }

      /**
       * Shows URL-only image popover (fallback when no media/upload client).
       * @param {HTMLButtonElement} btn
       * @param {import('@tiptap/core').Editor} edRef
       */
      function showImageUrlPopover(btn, edRef) {
        popovers.forEach(p => p.hide());
        const pop = createToolbarPopover(btn);
        popovers.push(pop);
        buildImagePopover(pop.el, {
          onApply(src, alt) {
            edRef.chain().focus().setImage({ src, alt }).run();
            pop.hide();
          },
          onCancel() { pop.hide(); },
        });
        pop.show();
      }

      // Roving tabindex on toolbar
      toolbarEl.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') {
          e.preventDefault();
          allBtns[rovingIdx].tabIndex = -1;
          rovingIdx = rovingIdx >= allBtns.length - 1 ? 0 : rovingIdx + 1;
          allBtns[rovingIdx].tabIndex = 0;
          allBtns[rovingIdx].focus();
        } else if (e.key === 'ArrowLeft') {
          e.preventDefault();
          allBtns[rovingIdx].tabIndex = -1;
          rovingIdx = rovingIdx <= 0 ? allBtns.length - 1 : rovingIdx - 1;
          allBtns[rovingIdx].tabIndex = 0;
          allBtns[rovingIdx].focus();
        } else if (e.key === 'Home') {
          e.preventDefault();
          allBtns[rovingIdx].tabIndex = -1;
          rovingIdx = 0;
          allBtns[0].tabIndex = 0;
          allBtns[0].focus();
        } else if (e.key === 'End') {
          e.preventDefault();
          allBtns[rovingIdx].tabIndex = -1;
          rovingIdx = allBtns.length - 1;
          allBtns[rovingIdx].tabIndex = 0;
          allBtns[rovingIdx].focus();
        }
      });

      // Active state tracking via editor transactions
      ed.on('transaction', () => {
        for (const btn of allBtns) {
          const cmd = btn.dataset.cmd;
          if (!cmd) continue;
          let active = false;
          switch (cmd) {
            case 'bold': active = ed.isActive('bold'); break;
            case 'italic': active = ed.isActive('italic'); break;
            case 'heading': active = ed.isActive('heading'); break;
            case 'bulletList': active = ed.isActive('bulletList'); break;
            case 'orderedList': active = ed.isActive('orderedList'); break;
            case 'blockquote': active = ed.isActive('blockquote'); break;
            case 'sourceView': continue; // managed by button handler
            case 'link': active = ed.isActive('link'); break;
            case 'alignLeft': active = ed.isActive({ textAlign: 'left' }); break;
            case 'alignCenter': active = ed.isActive({ textAlign: 'center' }); break;
            case 'alignRight': active = ed.isActive({ textAlign: 'right' }); break;
            case 'alignJustify': active = ed.isActive({ textAlign: 'justify' }); break;
          }
          btn.classList.toggle('rich-text-driver__toolbar-btn--active', active);
          if (btn.hasAttribute('aria-pressed')) {
            btn.setAttribute('aria-pressed', active ? 'true' : 'false');
          }
        }
      });
    }

    editorReady = true;
    applyDisabled();
  }).catch((err) => {
    if (destroyed) return;
    log.error('Rich text editor failed to load', { data: err });
    const errEl = document.createElement('p');
    errEl.className = 'rich-text-driver__error';
    errEl.textContent = 'Failed to load rich text editor.';
    wrapper.appendChild(errEl);
  });

  return {
    getValue() {
      if (!editor || !editorReady) return pendingValue;
      if (inSourceMode && sourceTextarea) return sourceTextarea.value;
      return editor.getHTML();
    },
    setValue(value) {
      const html = value == null ? '' : String(value);
      if (editor && editorReady) {
        ignoringUpdate = true;
        try {
          editor.commands.setContent(html, { emitUpdate: false });
        } finally {
          ignoringUpdate = false;
        }
        if (inSourceMode && sourceTextarea) {
          sourceTextarea.value = html;
        }
      } else {
        pendingValue = html;
      }
    },
    clearValue() {
      if (editor && editorReady) {
        ignoringUpdate = true;
        try {
          editor.commands.setContent('', { emitUpdate: false });
        } finally {
          ignoringUpdate = false;
        }
        if (inSourceMode && sourceTextarea) {
          sourceTextarea.value = '';
        }
      } else {
        pendingValue = '';
      }
    },
    setDisabled(disabled) {
      forcedDisabled = disabled;
      applyDisabled();
    },
    setVisible(visible) {
      wrapper.hidden = !visible;
    },
    setErrors() {
      // errors rendered by host
    },
    setAriaInvalid(invalid) {
      wrapper.setAttribute('aria-invalid', invalid ? 'true' : 'false');
    },
    destroy() {
      destroyed = true;
      // Destroy all popovers
      for (const pop of popovers) {
        pop.destroy();
      }
      popovers.length = 0;
      if (editor) {
        editor.destroy();
        editor = null;
      }
      wrapper.remove();
    },
  };
}
